import { useCallback, useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/cn';

/**
 * A modal dialog with a real focus trap, vendored rather than installed.
 *
 * The confirmation this exists for is the Done button, which hands a client's
 * work to a reviewer and locks their chat. That is a one-way door, so the
 * dialog has to behave properly: focus moves in, Escape and the backdrop close
 * it, Tab cannot escape, and focus returns where it came from.
 */

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  /** Blocks Escape and backdrop dismissal while an action is in flight. */
  busy?: boolean;
}

export function Dialog({ open, onClose, title, description, children, footer, busy }: DialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const descId = useId();

  const requestClose = useCallback(() => {
    if (!busy) onClose();
  }, [busy, onClose]);

  useEffect(() => {
    if (!open) return;
    returnFocusRef.current = document.activeElement as HTMLElement | null;

    const panel = panelRef.current;
    const first = panel?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? panel)?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        requestClose();
        return;
      }
      if (event.key !== 'Tab' || !panel) return;

      const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const firstEl = focusable[0]!;
      const lastEl = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === firstEl) {
        event.preventDefault();
        lastEl.focus();
      } else if (!event.shiftKey && document.activeElement === lastEl) {
        event.preventDefault();
        firstEl.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = previousOverflow;
      returnFocusRef.current?.focus?.();
    };
  }, [open, requestClose]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 grid place-items-center p-[22px]">
      <div
        className="absolute inset-0 bg-graphite/[0.34] backdrop-blur-[1px]"
        onClick={requestClose}
        aria-hidden
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        className={cn(
          'relative w-full max-w-[28rem] animate-panel-in overflow-hidden rounded-2xl bg-bone-raised p-2 shadow-sheet outline-none',
        )}
      >
        <div className="px-[22px] pb-0 pt-5">
          <h2 id={titleId} className="text-[1.4375rem] font-bold leading-tight tracking-[-0.025em] text-graphite-900">
            {title}
          </h2>
          {description ? (
            <div id={descId} className="mt-2.5 text-[0.9375rem] text-graphite-500">
              {description}
            </div>
          ) : null}
        </div>
        {children ? <div className="px-[22px] pb-4 pt-2.5">{children}</div> : null}
        {footer ? (
          <div className="flex flex-wrap items-center justify-end gap-2.5 px-3.5 pb-2 pt-4">
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
